import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bookmark, Download, FolderPlus, Lock, LockOpen, Play, ShieldCheck, Star, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { login, adminUsers, lockUser, unlockUser, deleteUser, adminActivity } from "../lib/api";
import { useAuth } from "../auth";
import { formatDate, PLATFORM_LABELS } from "../lib/youtube";

const Metric = ({ label, value }) => (
  <div className="flex flex-col items-center px-3 py-1.5">
    <span className="font-heading text-lg text-white leading-none">{value}</span>
    <span className="font-meta text-[9px] uppercase tracking-[0.15em] text-zinc-600 mt-1">{label}</span>
  </div>
);

const EVENT_ICONS = {
  registered: UserPlus,
  watched: Play,
  rated: Star,
  saved: Bookmark,
  folder: FolderPlus,
  imported: Download,
};

export default function Admin() {
  const { user, setUser } = useAuth();
  const [users, setUsers] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [activities, setActivities] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user && user.role === "admin";
  const load = () => adminUsers().then(setUsers).catch(() => setUsers([]));

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login({ email, password });
      setUser(u);
      if (u.role !== "admin") setError("This account is not an administrator");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const setLock = async (targetEmail, lock) => {
    try {
      await (lock ? lockUser(targetEmail) : unlockUser(targetEmail));
      toast.success(lock ? `${targetEmail} locked out` : `${targetEmail} unlocked`);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action failed");
    }
  };

  const toggleRow = async (targetEmail) => {
    if (expanded === targetEmail) {
      setExpanded(null);
      return;
    }
    setExpanded(targetEmail);
    if (!activities[targetEmail]) {
      const events = await adminActivity(targetEmail).catch(() => []);
      setActivities((a) => ({ ...a, [targetEmail]: events }));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget);
      toast.success(`${deleteTarget} and all their data deleted`);
      setDeleteTarget(null);
      if (expanded === deleteTarget) setExpanded(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div data-testid="admin-login" className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
        <motion.div
          className="absolute inset-0"
          style={{ background: "radial-gradient(circle at center, #18181b 0%, #000000 70%)" }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="flex items-center gap-3 justify-center mb-10">
            <ShieldCheck className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
            <span className="font-meta text-xs tracking-[0.4em] uppercase text-white">PurePlay Admin</span>
          </div>
          <div className="bg-[#050505] border border-white/10 rounded-2xl p-8 md:p-10">
            <h1 className="font-heading text-3xl text-white tracking-tight">Restricted area.</h1>
            <p className="mt-2 font-meta text-[11px] uppercase tracking-[0.25em] text-zinc-500">Administrator sign-in</p>
            <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
              <input
                data-testid="admin-email-input"
                type="email"
                placeholder="Admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-colors duration-300 font-ui text-sm"
              />
              <input
                data-testid="admin-password-input"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-colors duration-300 font-ui text-sm"
              />
              {error && (
                <p data-testid="admin-error" className="text-red-400 text-sm">{error}</p>
              )}
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading}
                className="mt-2 w-full bg-white text-black rounded-full py-3.5 font-meta text-xs uppercase tracking-[0.25em] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 disabled:opacity-50"
              >
                {loading ? "One moment…" : "Enter Control Room"}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const totals = (users || []).reduce(
    (acc, u) => ({
      watched: acc.watched + u.metrics.watched,
      saved: acc.saved + u.metrics.saved,
      imported: acc.imported + u.metrics.imported,
    }),
    { watched: 0, saved: 0, imported: 0 }
  );

  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto py-16 px-6 w-full">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <p className="font-meta text-[10px] uppercase tracking-[0.35em] text-zinc-600 mb-3">Control Room</p>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-white mb-4">Admin</h1>
        <p className="font-meta text-xs text-zinc-500 mb-4" data-testid="admin-totals">
          {users?.length ?? 0} USERS · {totals.watched} FILMS LOGGED · {totals.saved} SAVES · {totals.imported} IMPORTED WATCHES
        </p>
        <p className="font-meta text-[10px] text-zinc-700 uppercase tracking-[0.2em] mb-12">Click a user to see their activity timeline</p>
      </motion.div>

      <div className="flex flex-col gap-4">
        {(users || []).map((u, i) => (
          <motion.div
            key={u.user_id}
            data-testid={`admin-user-${u.email}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => toggleRow(u.email)}
            className={`border rounded-2xl p-5 md:p-6 cursor-pointer transition-colors duration-300 ${
              u.locked ? "border-red-500/30 bg-red-500/[0.03]" : "border-white/10 bg-white/[0.02] hover:border-white/20"
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex items-center gap-4 md:w-72 shrink-0">
                {u.picture ? (
                  <img src={u.picture} alt="" referrerPolicy="no-referrer" className="w-11 h-11 rounded-full border border-white/20 object-cover" />
                ) : (
                  <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center font-meta text-xs text-zinc-300 shrink-0">
                    {(u.first_name?.[0] || "?").toUpperCase()}
                    {(u.last_name?.[0] || "").toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="font-heading text-lg text-white truncate flex items-center gap-2">
                    {u.first_name} {u.last_name}
                    {u.role === "admin" && (
                      <span className="font-meta text-[9px] tracking-[0.2em] text-amber-400 border border-amber-400/30 rounded-full px-2 py-0.5">ADMIN</span>
                    )}
                    {u.locked && (
                      <span data-testid={`locked-badge-${u.email}`} className="font-meta text-[9px] tracking-[0.2em] text-red-400 border border-red-500/30 rounded-full px-2 py-0.5">LOCKED</span>
                    )}
                  </p>
                  <p className="font-meta text-[11px] text-zinc-500 truncate">{u.email}</p>
                  <p className="font-meta text-[10px] text-zinc-700 mt-0.5">Joined {formatDate(u.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-1 flex-1">
                <Metric label="Watched" value={u.metrics.watched} />
                <Metric label="Saved" value={u.metrics.saved} />
                <Metric label="Folders" value={u.metrics.folders} />
                <Metric label="Imported" value={u.metrics.imported} />
                <Metric label="Rated" value={u.metrics.rated} />
                <Metric label="Avg ★" value={u.metrics.avg_rating} />
              </div>
              {u.role !== "admin" && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`${u.locked ? "unlock" : "lock"}-user-${u.email}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLock(u.email, !u.locked);
                    }}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full border font-meta text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                      u.locked
                        ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        : "border-red-500/30 text-red-400 hover:bg-red-500/10"
                    }`}
                  >
                    {u.locked ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    {u.locked ? "Unlock" : "Lock"}
                  </button>
                  <button
                    data-testid={`delete-user-${u.email}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(u.email);
                    }}
                    aria-label="Delete user"
                    className="p-2.5 rounded-full border border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-500/40 transition-colors duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>

            <AnimatePresence>
              {expanded === u.email && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div data-testid={`activity-${u.email}`} className="mt-6 pt-6 border-t border-white/5 flex flex-col gap-3">
                    {!activities[u.email] ? (
                      <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-600">Loading activity…</p>
                    ) : activities[u.email].length === 0 ? (
                      <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-600">No activity yet</p>
                    ) : (
                      activities[u.email].map((ev, j) => {
                        const Icon = EVENT_ICONS[ev.type] || Play;
                        return (
                          <div key={j} className="flex items-center gap-4 text-sm">
                            <span className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                              <Icon className="w-3 h-3 text-zinc-400" />
                            </span>
                            <span className="text-zinc-300 truncate flex-1">{ev.label}</span>
                            {ev.platform && (
                              <span className="font-meta text-[9px] tracking-[0.15em] text-zinc-600 border border-white/10 rounded-full px-2 py-0.5 shrink-0">
                                {PLATFORM_LABELS[ev.platform] || ev.platform}
                              </span>
                            )}
                            <span className="font-meta text-[10px] text-zinc-600 shrink-0">{formatDate(ev.at)}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
        {users && users.length === 0 && (
          <p className="text-center text-zinc-600 font-heading text-xl py-16">No registered users yet.</p>
        )}
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            data-testid="delete-user-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteTarget(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 max-w-sm w-full"
            >
              <h3 className="font-heading text-2xl text-white tracking-tight">Delete this account?</h3>
              <p className="mt-3 text-sm text-zinc-500">
                <span className="text-zinc-300">{deleteTarget}</span> and all their data — videos, saves, folders, imports — will be permanently removed. This cannot be undone.
              </p>
              <div className="mt-8 flex justify-end gap-3">
                <button
                  data-testid="cancel-delete-user"
                  onClick={() => setDeleteTarget(null)}
                  className="px-5 py-2.5 rounded-full border border-white/10 font-meta text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:border-white/30 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  data-testid="confirm-delete-user"
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="px-5 py-2.5 rounded-full bg-red-500 font-meta text-[11px] uppercase tracking-[0.2em] text-white hover:bg-red-600 transition-colors duration-200 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete forever"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
